import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import type { QueuedReportJobResponse, QueuedReportJobStatus } from '~/services/ReportingService';

import { reportingQueryKeys } from './reportingQueryKeys';

const TERMINAL_STATUSES = new Set(['completed', 'failed']);

/** Shared queue, poll, and download lifecycle for user-initiated email reports. */
export function useQueuedReport<TParams>(
  reportKey: string,
  queueReport: (params: TParams) => Promise<QueuedReportJobResponse>,
  getStatus: (jobId: string, options?: RequestInit) => Promise<QueuedReportJobStatus>,
  failureMessage: string,
) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const mutation = useMutation({ mutationFn: queueReport });
  const status = useQuery({
    queryKey: reportingQueryKeys.queuedReportJob(reportKey, jobId ?? ''),
    queryFn: ({ signal }) => getStatus(jobId!, { signal }),
    enabled: Boolean(jobId),
    retry: 1,
    refetchInterval: (query): number | false => {
      if (query.state.error) return false;
      const state = query.state.data?.status;
      return state && TERMINAL_STATUSES.has(state) ? false : 3000;
    },
  });

  useEffect(() => {
    if (!status.data) return;
    if (status.data.downloadUrl) setDownloadUrl(status.data.downloadUrl);
    if (status.data.status === 'completed') {
      setCompleted(true);
      setJobId(null);
    } else if (status.data.status === 'failed') {
      setError(status.data.error || failureMessage);
      setJobId(null);
    }
  }, [status.data, failureMessage]);

  useEffect(() => {
    if (!status.error) return;
    setError(status.error instanceof Error ? status.error.message : 'Failed to check report status');
    setJobId(null);
  }, [status.error]);

  const isRunning = Boolean(jobId) || mutation.isPending;

  async function start(params: TParams) {
    if (isRunning) return;
    setError(null);
    setCompleted(false);
    setDownloadUrl(null);
    try {
      const response = await mutation.mutateAsync(params);
      if (!response.jobId) throw new Error('The report queue did not return a job ID');
      setJobId(response.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : failureMessage);
    }
  }

  return {
    start,
    isRunning,
    hasResults: Boolean(jobId && status.data?.hasResults),
    completed,
    error,
    downloadUrl,
  };
}
